import type {
  CreateAppointmentProfileRequest,
  Speciality,
  SpecialityWire,
} from '@/types/scheduling-api';

/**
 * Quick-create speciality override — Flutter parity rule (PROD-829).
 *
 * In Flutter, `calendar_tab.dart:4851-4858` does this on the
 * appointment-profile-chip click path:
 *
 * ```dart
 * doctor.speciality = appointmentProfile.speciality ?? doctorList[index].speciality;
 * doctor.specialityId = appointmentProfile.speciality?.specialitySkillId ??
 *     doctorList[index].speciality?.specialitySkillId;
 * ```
 *
 * **Applied to ALL THREE quick-create paths** (popover slot, "Next"
 * inline slot, profile chip) — Flutter only does this for the
 * profile-chip path, but for multi-speciality providers the
 * appointment record's speciality must come from the profile being
 * booked, not from the provider's first-listed speciality. Confirmed
 * in scope with the user 2026-05-19.
 *
 * The "chosen profile" varies per path:
 *   - Popover slot   → preferred profile
 *   - Next inline    → preferred profile
 *   - Profile chip   → the specific profile clicked
 *
 * Returns the pieces the form needs (display string + SNOMED-shaped
 * code for the booking payload). Both fall back to the provider's
 * own speciality when the profile has none.
 *
 * Type note: at the type level `Speciality.code` is `Coding` (with
 * `display`) and `SpecialityWire.code` is `SpecialityCoding` (with
 * `displayName`). The runtime shape from different endpoints has
 * both fields interchangeably — same loose-cast pattern as
 * `provider-section.tsx`.
 */

export interface SpecialityOverride {
  providerSpeciality: string | null;
  providerSpecialityCode: SpecialityWire | null;
}

interface ProviderSpecialityInputs {
  /** Provider's own speciality (display string already extracted). */
  providerSpecialityDisplay?: string | null;
  /** Provider's own speciality wire shape (for the SNOMED code).
   *  Optional — `provider-section.tsx` casts when reading. */
  providerSpeciality?: Speciality | null;
}

/** Loose-cast view onto the runtime speciality code shape. */
interface RuntimeSpecialityCode {
  code?: string;
  display?: string;
  displayName?: string;
}
interface RuntimeSpeciality {
  specialitySkillId?: string;
  code?: RuntimeSpecialityCode;
  display?: string;
}

export function applySpecialityOverride(
  profile: CreateAppointmentProfileRequest,
  provider: ProviderSpecialityInputs,
): SpecialityOverride {
  const profSpec = profile.speciality as RuntimeSpeciality | undefined;
  // Display string: profile.code.displayName → .code.display → .display → provider's.
  const profileDisplay =
    profSpec?.code?.displayName ??
    profSpec?.code?.display ??
    profSpec?.display ??
    null;
  const providerDisplay = provider.providerSpecialityDisplay ?? null;
  const providerSpeciality = profileDisplay ?? providerDisplay;

  // Wire-shape SNOMED code. Profile's code wins, fall back to provider's.
  const providerWire = provider.providerSpeciality as RuntimeSpeciality | undefined;

  const profileCodeBlock: SpecialityWire | null = profSpec?.code?.code
    ? {
        specialitySkillId: profSpec.specialitySkillId,
        code: {
          code: profSpec.code.code,
          displayName: profSpec.code.displayName ?? profSpec.code.display,
          system: 'https://snomed.info/sct',
          description: null,
          shortName: null,
        },
      }
    : null;
  const providerCodeBlock: SpecialityWire | null = providerWire?.code?.code
    ? {
        specialitySkillId: providerWire.specialitySkillId,
        code: {
          code: providerWire.code.code,
          displayName: providerWire.code.displayName ?? providerWire.code.display,
          system: 'https://snomed.info/sct',
          description: null,
          shortName: null,
        },
      }
    : null;

  return {
    providerSpeciality,
    providerSpecialityCode: profileCodeBlock ?? providerCodeBlock,
  };
}
